import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DateInput, SegmentedControl } from '@/components/ui'
import type { AnalyticsPeriodMode } from '@/features/expenses/types/expense.types'
import { isCurrentPeriod, periodLabel, shiftAnchor } from './periodNav'

const PERIOD_MODES: { value: AnalyticsPeriodMode; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'range', label: 'Custom' },
]

const THIS_PERIOD: Record<Exclude<AnalyticsPeriodMode, 'range'>, string> = {
  week: 'This week',
  month: 'This month',
  year: 'This year',
}

const stepButton =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'

export interface ExpensePeriodPickerProps {
  mode: AnalyticsPeriodMode
  onModeChange: (mode: AnalyticsPeriodMode) => void
  anchor: string
  onAnchorChange: (anchor: string) => void
  rangeFrom: string
  rangeTo: string
  onRangeChange: (range: { from: string; to: string }) => void
  /** Today as 'YYYY-MM-DD', for the "This month" shortcut. */
  today: string
}

/**
 * Week / Month / Year / Custom, then the period itself: stepped with arrows
 * for a week, month or year, or two dates for a custom range. A day picker
 * standing in for "which month" asked for more than the question needed.
 */
export function ExpensePeriodPicker({
  mode,
  onModeChange,
  anchor,
  onAnchorChange,
  rangeFrom,
  rangeTo,
  onRangeChange,
  today,
}: ExpensePeriodPickerProps): React.ReactElement {
  return (
    // Stacked on phones: on one line the toolbar would push the stepper off-screen.
    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
      <SegmentedControl
        ariaLabel="Period"
        options={PERIOD_MODES}
        value={mode}
        onChange={onModeChange}
      />
      {mode === 'range' ? (
        <div className="flex items-center gap-2 [&>div]:min-w-0 [&>div]:flex-1 sm:[&>div]:flex-none">
          <DateInput
            className="h-10 w-full rounded-lg sm:w-40"
            aria-label="From date"
            value={rangeFrom}
            max={rangeTo || undefined}
            onChange={(e) => onRangeChange({ from: e.target.value, to: rangeTo })}
          />
          <span className="text-sm text-gray-400" aria-hidden>
            –
          </span>
          <DateInput
            className="h-10 w-full rounded-lg sm:w-40"
            aria-label="To date"
            value={rangeTo}
            min={rangeFrom || undefined}
            onChange={(e) => onRangeChange({ from: rangeFrom, to: e.target.value })}
          />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <div className="flex h-10 flex-1 items-center justify-between rounded-lg sm:flex-none border border-gray-200 bg-white px-0.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <button
              type="button"
              className={stepButton}
              onClick={() => onAnchorChange(shiftAnchor(mode, anchor, -1))}
              aria-label={`Previous ${mode}`}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <span
              className="min-w-[9.5rem] px-1 text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100"
              aria-live="polite"
            >
              {periodLabel(mode, anchor)}
            </span>
            <button
              type="button"
              className={stepButton}
              onClick={() => onAnchorChange(shiftAnchor(mode, anchor, 1))}
              aria-label={`Next ${mode}`}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {!isCurrentPeriod(mode, anchor, today) && (
            <button
              type="button"
              onClick={() => onAnchorChange(today)}
              className="h-10 rounded-lg px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {THIS_PERIOD[mode]}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
